import { R_LINK_MIN, T_RESOLVE_MIN, topPickRescueForFriction } from '../constants';
import type { FrictionSubtype, InterruptionSource, Ping, PingType, RescueKind, RescueSession } from '../types';

type RescueStats = {
  completed: number;
  resolved: number;
  lastTs: number;
};

type RescueStatsMap = Map<RescueKind, RescueStats>;

export function personalizedTopPickRescueForFriction(
  pings: Ping[],
  rescues: RescueSession[],
  type: PingType,
  subtype: FrictionSubtype | null | undefined,
  interruptionSource: InterruptionSource | null | undefined
): RescueKind {
  const fallback = topPickRescueForFriction(type, subtype, interruptionSource);
  if (pings.length === 0 || rescues.length === 0) return fallback;

  const pingById = new Map(pings.map((ping) => [ping.id, ping]));
  const nextPingAfterTs = buildNextPingFinder(pings);
  const byType = new Map<PingType, RescueStatsMap>();
  const bySubtype = new Map<FrictionSubtype, RescueStatsMap>();
  const byInterruption = new Map<InterruptionSource, RescueStatsMap>();

  for (const rescue of rescues) {
    if (!rescue.ping_id) continue;
    const ping = pingById.get(rescue.ping_id);
    if (!ping) continue;
    if (rescue.ts_start_ms - ping.ts_ms > R_LINK_MIN * 60 * 1000) continue;
    const completed = rescue.completed === 1 && rescue.ts_end_ms !== null;
    if (!completed) continue;

    const endMs = rescue.ts_end_ms as number;
    const nextPingTs = nextPingAfterTs(endMs);
    const resolved = nextPingTs === null || nextPingTs - endMs > T_RESOLVE_MIN * 60 * 1000;

    upsertStat(byType, ping.type, rescue.kind, endMs, resolved);
    if (ping.subtype) upsertStat(bySubtype, ping.subtype, rescue.kind, endMs, resolved);
    if (ping.interruption_source) upsertStat(byInterruption, ping.interruption_source, rescue.kind, endMs, resolved);
  }

  const subtypePick = subtype ? pickBest(bySubtype.get(subtype), 2) : null;
  if (subtypePick) return subtypePick;
  const interruptionPick = interruptionSource ? pickBest(byInterruption.get(interruptionSource), 2) : null;
  if (interruptionPick) return interruptionPick;
  const typePick = pickBest(byType.get(type), 3);
  return typePick ?? fallback;
}

function buildNextPingFinder(pings: Ping[]): (ts: number) => number | null {
  const sorted = [...pings].sort((a, b) => a.ts_ms - b.ts_ms).map((ping) => ping.ts_ms);
  return (ts: number) => {
    let lo = 0;
    let hi = sorted.length - 1;
    let answer: number | null = null;
    while (lo <= hi) {
      const mid = (lo + hi) >> 1;
      const value = sorted[mid];
      if (value > ts) {
        answer = value;
        hi = mid - 1;
      } else {
        lo = mid + 1;
      }
    }
    return answer;
  };
}

function upsertStat<K extends string>(index: Map<K, RescueStatsMap>, key: K, kind: RescueKind, ts: number, resolved: boolean): void {
  const byRescue = index.get(key) ?? new Map<RescueKind, RescueStats>();
  const current = byRescue.get(kind) ?? { completed: 0, resolved: 0, lastTs: 0 };
  current.completed += 1;
  if (resolved) current.resolved += 1;
  current.lastTs = Math.max(current.lastTs, ts);
  byRescue.set(kind, current);
  index.set(key, byRescue);
}

function pickBest(statsByRescue: RescueStatsMap | undefined, minCompleted: number): RescueKind | null {
  if (!statsByRescue) return null;
  let bestKind: RescueKind | null = null;
  let bestScore = -1;
  let bestCompleted = -1;
  let bestLastTs = -1;

  for (const [kind, stat] of statsByRescue.entries()) {
    if (stat.completed < minCompleted) continue;
    const score = (stat.resolved + 1) / (stat.completed + 2);
    if (score > bestScore) {
      bestKind = kind;
      bestScore = score;
      bestCompleted = stat.completed;
      bestLastTs = stat.lastTs;
      continue;
    }
    if (score === bestScore && stat.completed > bestCompleted) {
      bestKind = kind;
      bestCompleted = stat.completed;
      bestLastTs = stat.lastTs;
      continue;
    }
    if (score === bestScore && stat.completed === bestCompleted && stat.lastTs > bestLastTs) {
      bestKind = kind;
      bestLastTs = stat.lastTs;
    }
  }
  return bestKind;
}
